"use client";

import React, { useState } from "react";
import PropTypes from "prop-types";
import styles from "../css/components/formInput.module.css";

const FormInput = ({ label, type = "text", name, value, onChange, onBlur, placeholder = "", className = "", inputClassName = "", prefix = null, formatAsDate = false, ...rest }) => {
  const [fileName, setFileName] = useState("No file chosen");

  const formatDateDmy = (raw) => {
    const digits = String(raw || "").replace(/\D/g, "").slice(0, 8);
    const d = digits.slice(0, 2);
    const m = digits.slice(2, 4);
    const y = digits.slice(4, 8);
    if (digits.length <= 2) return d;
    if (digits.length <= 4) return `${d}-${m}`;
    return `${d}-${m}-${y}`;
  };

  const handleChange = (e) => {
    if (!formatAsDate) {
      onChange && onChange(e);
      return;
    }

    const nextValue = formatDateDmy(e?.target?.value);
    onChange && onChange({ ...e, target: { ...e.target, name, value: nextValue } });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    setFileName(file ? file.name : "No file chosen");

    // Propagate to parent handler
    onChange && onChange(e);
  };

  const handleKeyDown = (e) => {
    // For number inputs, disallow typing '-' and 'e'/'E' and '+' to avoid negative/exp notation
    if (type === "number") {
      if (e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+") {
        e.preventDefault();
        return;
      }
    }
    // Pass through to consumer if provided in rest
    if (typeof rest.onKeyDown === 'function') {
      rest.onKeyDown(e);
    }
  };

  return (
    <div className={`${styles.formGroup} ${className}`}>
      {label && (
        <label htmlFor={name} className={styles.formLabel}>
          {label}
        </label>
      )}

      {type === "file" ? (
        <div className={styles.fileInputWrapper}>
          <input
            id={name}
            name={name}
            type="file"
            onChange={handleFileChange}
            onBlur={onBlur}
            className={styles.fileInputNative}
          />
          <span className={styles.fileName}>{fileName}</span>
        </div>
      ) : (
        prefix ? (
          <div className={styles.inputWrapper}>
            <span className={styles.prefix}>{prefix}</span>
            <input
              id={name}
              type={type}
              name={name}
              value={value}
              onChange={handleChange}
              onBlur={onBlur}
              placeholder={placeholder}
              className={`${styles.formInput} ${styles.withPrefix} ${inputClassName}`}
              onKeyDown={handleKeyDown}
              {...rest}
            />
          </div>
        ) : (
          <input
            id={name}
            type={type}
            name={name}
            value={value}
            onChange={handleChange}
            onBlur={onBlur}
            placeholder={placeholder}
            className={`${styles.formInput} ${inputClassName}`}
            onKeyDown={handleKeyDown}
            {...rest}
          />
        )
      )}
    </div>
  );
};

FormInput.propTypes = {
  label: PropTypes.string,
  type: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  prefix: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  formatAsDate: PropTypes.bool,
};

export default FormInput;
